import {
  WSActions,
  WSCard,
  WSControl,
  WSEmptyState,
  WSForm,
  WSFormProps,
  WSInfoBox,
  WSInputNumber,
  WSList,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import merge from "lodash/merge";
import { WSFormErrorSummary } from "../../../../shared/components/WSFormErrorSummary";
import { FormPartialDataPerson } from "../FormPartialPerson";
import { FormPartialTaxId } from "../FormPartialTaxId";
import { BeneficialOwners } from "../FormRepresentative/BeneficialOwners";
import { calculateTotalStake } from "../FormRepresentative/helpers";

export type FormDataAdditionalInfoRepresentative = {
  representative: {
    taxId: string;
    ownershipStake: number;
    representativeConfirmation: boolean;
  };
  beneficialOwners: FormPartialDataPerson[];
  allOwnersAdded: boolean;
};

type Props = {
  defaultValues?: WSFormProps<FormDataAdditionalInfoRepresentative>["defaultValues"];
  onSubmit: WSFormProps<FormDataAdditionalInfoRepresentative>["onSubmit"];
  onBack?: () => void;
  country: string;
};

export const FormAdditionalInfoRepresentative: React.FC<Props> = props => {
  const emptyValues = {
    representative: {
      ownershipStake: 100
    },
    beneficialOwners: [],
    allOwnersAdded: false
  };

  return (
    <WSForm<FormDataAdditionalInfoRepresentative>
      defaultValues={merge(emptyValues, props.defaultValues)}
      onSubmit={props.onSubmit}
    >
      <WSList gap="2XL">
        <WSEmptyState
          type="people"
          title="Additional information required for banking verification"
          description="In order to activate banking features (such as Tax Withholding or Wingspan Wallet), we need additional information per regulatory requirements. Complete the additional information below."
          hasBorder
          orientation="horizontal"
          bgColor="white"
        />

        <WSCard>
          <WSList gap="M">
            <WSInfoBox>
              List all individuals who own at least 25% of the business or have
              substantial control over the business.
            </WSInfoBox>

            <WSSectionToolbar title="Representative information (You)" />

            <WSList gap="2XL">
              <FormPartialTaxId
                type="Individual"
                country={props.country}
                name="representative.taxId"
                requireTaxId
              />

              <WSForm.Context>
                {formContext => (
                  <WSForm.Field
                    name="representative.ownershipStake"
                    label="Ownership stake"
                    onChange={() => {
                      formContext.trigger("representative.ownershipStake");
                    }}
                    component={WSInputNumber}
                    componentProps={{
                      required: true,
                      iconRight: "percent",
                      min: 0,
                      max: 100,
                      helperText: `Enter the approximate percentage (%) ownership stake you have in the business`,
                      status: formContext.errors.representative?.ownershipStake
                        ? "error"
                        : undefined,
                      message:
                        formContext.errors.representative?.ownershipStake
                          ?.message
                    }}
                  />
                )}
              </WSForm.Context>

              <WSForm.Value name="representative.ownershipStake">
                {ownershipStake => {
                  if (ownershipStake !== null && ownershipStake < 25) {
                    return (
                      <WSInfoBox title="Important:">
                        <p>
                          You need to be a beneficial owner (25% ownership or
                          more) or an authorized representative to verify your
                          business
                        </p>

                        <p>
                          If you are not a beneficial owner or authorized
                          representative of the business (such as a controller
                          or officer), we recommend having either a beneficial
                          owner or authorized representative of the business
                          setup the account instead. Otherwise, we cannot
                          successfully verify your business.
                        </p>

                        <WSForm.Field
                          name="representative.representativeConfirmation"
                          component={WSControl}
                          componentProps={{
                            type: "checkbox",
                            size: "S",
                            label:
                              "I confirm that I’m an authorized representative of the business with less than 25% ownership stake."
                          }}
                        />
                      </WSInfoBox>
                    );
                  }

                  return null;
                }}
              </WSForm.Value>
            </WSList>
          </WSList>
        </WSCard>

        <WSForm.Value name="representative.ownershipStake">
          {representativeOwnershipStake => {
            const beneficialOwnersVisible =
              !representativeOwnershipStake ||
              representativeOwnershipStake <= 75;

            return (
              <BeneficialOwners
                hidden={!beneficialOwnersVisible}
                country={props.country}
              />
            );
          }}
        </WSForm.Value>

        <WSFormErrorSummary />

        <WSForm.Values names={["representative", "beneficialOwners"]}>
          {values => {
            const representativeOwnershipStake =
              values.representative?.ownershipStake || 0;
            const beneficialOwners = values.beneficialOwners || 0;

            const stakesSum = calculateTotalStake(
              representativeOwnershipStake,
              beneficialOwners
            );

            return (
              <WSActions
                alignment="fill"
                buttons={[
                  {
                    label: "Back",
                    onClick: props.onBack,
                    kind: "Secondary",
                    type: "button",
                    visible: !!props.onBack
                  },
                  {
                    label: "Continue",
                    disabled: stakesSum > 100
                  }
                ]}
              />
            );
          }}
        </WSForm.Values>
      </WSList>
    </WSForm>
  );
};
